import {
	AppointmentDayOfWeek,
	AppointmentDetailModel,
	AppointmentIdentifier,
	AppointmentRecurrenceRangeKind,
	CalendarColor,
	CalendarKind,
	CreateCalendarAppointmentModel,
	ScheduleAvailabilityKind,
	RecurringAppointmentOperationKind,
	UpdateCalendarAppointmentModel,
	WorkspaceCalendarConfiguration
} from "client/Api/Calendar/calendar";
import { SuggestedMultiComboBoxItem } from "client/Components";
import { DisplayContactModelWithoutId } from "./AttendeesInput";

type CalendarItemBase = {
	id: string;
	name: string;
	kind: CalendarKind;
	isDefault: boolean;
	canEdit: boolean;
	// NOTE: configuration is separate from isEnabled and color, the reason here is that
	// while we are enabling the calendar (i.e. retrieving the id of the configuration),
	// we already want to load the appointments / have the calendar enabled in the sidebar
	configuration: WorkspaceCalendarConfiguration | null;
};

export type EnabledCalendarItem = CalendarItemBase & {
	isEnabled: true;
	color: CalendarColor;
};

export type DisabledCalendarItem = CalendarItemBase & {
	isEnabled: false;
};

export type CalendarItem = EnabledCalendarItem | DisabledCalendarItem;

type ComposeAppointmentModel = ({ id: string; } & UpdateCalendarAppointmentModel)
	| ({ id?: undefined; } & CreateCalendarAppointmentModel);

export enum RecurrencePatternKind {
	DontRepeat = 0,
	Daily = 1,
	Weekly = 2,
	AbsoluteMonthly = 3,
	RelativeMonthly = 4,
	RelativeMonthlyLastWeek = 5,
	Yearly = 6,
	Custom = 7
}

export type WeeklyCustomRecurrenceOptionsModel = {
	daysOfWeek: readonly AppointmentDayOfWeek[];
};

export type MonthlyCustomRecurrenceKind =
	| "absolute"
	| "relative"
	| "relative-last-week";

export type MonthlyCustomRecurrenceOptionsModel = {
	kind: MonthlyCustomRecurrenceKind;
};

export type YearlyCustomRecurrenceKind =
	| "absolute"
	| "relative";

export type YearlyCustomRecurrenceOptionsModel = {
	kind: YearlyCustomRecurrenceKind;
};

export type RecurrenceRangeFormModel = {
	kind: Exclude<AppointmentRecurrenceRangeKind, AppointmentRecurrenceRangeKind.Numbered>;
	endDate: Date | null;
};

export enum CustomRecurrencePatternKind {
	Daily = 0,
	Weekly = 1,
	Monthly = 2,
	Yearly = 3
};

export type CustomRecurrenceOptionsModel = {
	repeatInterval: number;
	kind: CustomRecurrencePatternKind;
	weeklyCustomRecurrenceOptions: WeeklyCustomRecurrenceOptionsModel;
	monthlyCustomRecurrenceOptions: MonthlyCustomRecurrenceOptionsModel;
	yearlyCustomRecurrenceOptions: YearlyCustomRecurrenceOptionsModel;
};

export type RecurrenceOptionsFormModel = {
	recurrencePatternKind: RecurrencePatternKind;
	customRecurrenceOptions: CustomRecurrenceOptionsModel;
	range: RecurrenceRangeFormModel;
};

export type AppointmentLocationFormData = {
	displayName: string;
} & ({
	id: string;
	availabilityKind: ScheduleAvailabilityKind | null;
} | {
	id?: undefined;
});

export type CalendarAppointmentFormModel = Omit<
	ComposeAppointmentModel,
	"attendees"
		| "locations"
		| "calendarId"
		| "startTime"
		| "endTime"
		| "reminderMinutes"
		| "isPrivate"
		| "recurrenceOptions"
		| "recurrenceTarget"
		| "isTeamsMeeting"
> & {
	attendees: readonly SuggestedMultiComboBoxItem<DisplayContactModelWithoutId>[];
	locations: readonly SuggestedMultiComboBoxItem<AppointmentLocationFormData>[];
	calendarId: string | null;
	startTime: Date | null;
	endTime: Date | null;
	isPrivate: OptionalModel<boolean>;
	reminderMinutes: OptionalModel<number | null>;
	recurringOperationKind?: RecurringAppointmentOperationKind;
	recurrenceOptions: RecurrenceOptionsFormModel;
	isTeamsMeeting: OptionalModel<boolean>;
};

export type AppointmentOrIdentifier = AppointmentIdentifier | AppointmentInformation;

export type BasicAppointmentInformation = {
	kind: "basic";
	subject: string | null;
	start: Date | string;
	end: Date | string;
	isAllDayEvent: boolean;
};

export type DetailsAppointmentInformation = {
	kind: "details";
} & AppointmentDetailModel;

export type AppointmentInformation =
	| BasicAppointmentInformation
	| DetailsAppointmentInformation;

export function isAppointmentInformation(
	item: AppointmentOrIdentifier
): item is AppointmentInformation {
	return "kind" in item;
}